import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { PurchaseCostCategoryRecord } from "@/lib/catalog/purchaseCostCategoriesStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";

type PurchaseCostCategorySelectProps = {
  value: string;
  categories: PurchaseCostCategoryRecord[];
  onChange: (id: string) => void;
  placeholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
};

export function PurchaseCostCategorySelect({
  value,
  categories,
  onChange,
  placeholder = "Select category",
  triggerClassName,
  disabled = false,
}: PurchaseCostCategorySelectProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => categories.find((c) => c.id === value) ?? null,
    [categories, value],
  );

  const displayLabel = selected ? selected.name : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "glass-input h-10 w-full justify-between px-3 font-normal text-sm",
            !selected && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[100]" align="start" sideOffset={4}>
        <Command shouldFilter>
          <CommandInput placeholder="Search category..." className="h-9 text-xs" />
          <CommandList className="max-h-[240px] overflow-y-auto">
            <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
              No category found.
            </CommandEmpty>
            <CommandGroup>
              {categories.map((c) => {
                const isSelected = c.id === value;
                return (
                  <CommandItem
                    key={c.id}
                    value={c.name}
                    onSelect={() => {
                      onChange(c.id);
                      setOpen(false);
                    }}
                    className="text-xs flex items-center justify-between cursor-pointer"
                  >
                    <span className="truncate font-medium">{c.name}</span>
                    <Check
                      className={cn(
                        "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
